import { Head, Link } from "@inertiajs/react"
import { ArrowLeft, Ban, CheckCircle, Eye, Trash2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import AdminLayout from "@/layouts/admin-layout"
import { typedPatch } from "@/lib/typed-router"
import type { AdminUpdateReport } from "@/types/router-mutations"
import type { AdminReportsShow } from "@/types/serializers"

type Props = AdminReportsShow

const STATUS_LABELS: Record<string, string> = {
  pendiente: "Pendiente",
  revisado: "Revisado",
  resuelto: "Resuelto",
}

const REASON_LABELS: Record<string, string> = {
  spam: "Spam",
  acoso: "Acoso",
  contenido_inapropiado: "Contenido inapropiado",
  perfil_falso: "Perfil falso",
  otro: "Otro",
}

function StatusBadge({ status }: { status: string }) {
  switch (status) {
    case "pendiente":
      return (
        <Badge variant="destructive">{STATUS_LABELS[status] || status}</Badge>
      )
    case "revisado":
      return (
        <Badge className="border-orange-400/30 bg-orange-500/20 text-orange-400">
          {STATUS_LABELS[status] || status}
        </Badge>
      )
    case "resuelto":
      return (
        <Badge variant="outline" className="border-green-400/30 text-green-400">
          {STATUS_LABELS[status] || status}
        </Badge>
      )
    default:
      return <Badge variant="secondary">{status}</Badge>
  }
}

function handleUpdateStatus(reportId: number, status: string, action?: string) {
  const params: AdminUpdateReport["report"] = { status }
  if (action) params.action = action

  typedPatch<AdminUpdateReport>(`/admin/reports/${reportId}`, {
    report: params,
  })
}

export default function AdminReportsShow({
  report,
  reportable,
  reported_user,
}: Props) {
  return (
    <AdminLayout>
      <Head title={`Reporte #${report.id} | Admin KrownFT`} />

      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link
            href="/admin/reports"
            className="text-muted-foreground hover:text-foreground flex items-center gap-1 text-sm"
          >
            <ArrowLeft className="h-4 w-4" />
            Volver
          </Link>
        </div>

        <div className="flex items-center gap-3">
          <h1 className="text-foreground text-2xl font-bold">
            Reporte #{report.id}
          </h1>
          <StatusBadge status={report.status} />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Detalles del Reporte</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  Motivo
                </p>
                <Badge variant="secondary" className="mt-1">
                  {REASON_LABELS[report.reason] || report.reason}
                </Badge>
              </div>
              {report.description && (
                <div>
                  <p className="text-muted-foreground text-sm font-medium">
                    Descripción
                  </p>
                  <p className="text-foreground mt-1 text-sm">
                    {report.description}
                  </p>
                </div>
              )}
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  Tipo de contenido
                </p>
                <p className="text-foreground mt-1 text-sm">
                  {report.reportable_type}
                </p>
              </div>
              <div>
                <p className="text-muted-foreground text-sm font-medium">
                  Fecha
                </p>
                <p className="text-foreground mt-1 text-sm">
                  {new Date(report.created_at).toLocaleString("es-AR")}
                </p>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-base">Reportado por</CardTitle>
            </CardHeader>
            <CardContent className="space-y-2">
              <p className="text-foreground font-medium">
                {report.reporter.full_name}
              </p>
              <p className="text-muted-foreground text-sm">
                @{report.reporter.nickname}
              </p>
              <p className="text-muted-foreground text-sm">
                {report.reporter.email}
              </p>
            </CardContent>
          </Card>

          {reportable && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Contenido Reportado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {reportable.body && (
                  <p className="text-foreground text-sm">{reportable.body}</p>
                )}
                {reportable.title && (
                  <p className="text-foreground text-sm font-medium">
                    {reportable.title}
                  </p>
                )}
                {reportable.description && !reportable.body && (
                  <p className="text-foreground text-sm">
                    {reportable.description}
                  </p>
                )}
                <p className="text-muted-foreground text-xs">
                  Creado:{" "}
                  {new Date(reportable.created_at).toLocaleString("es-AR")}
                </p>
              </CardContent>
            </Card>
          )}

          {reported_user && (
            <Card>
              <CardHeader>
                <CardTitle className="text-base">Usuario Reportado</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <p className="text-foreground font-medium">
                  {reported_user.full_name}
                </p>
                <p className="text-muted-foreground text-sm">
                  @{reported_user.nickname}
                </p>
                <p className="text-muted-foreground text-sm">
                  {reported_user.email}
                </p>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">{reported_user.role}</Badge>
                  {reported_user.suspended_at ? (
                    <Badge variant="destructive">Suspendido</Badge>
                  ) : (
                    <Badge
                      variant="outline"
                      className="border-green-400/30 text-green-400"
                    >
                      Activo
                    </Badge>
                  )}
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        {report.status !== "resuelto" && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Acciones</CardTitle>
            </CardHeader>
            <CardContent className="flex flex-wrap gap-3">
              {report.status === "pendiente" && (
                <Button
                  variant="outline"
                  onClick={() => handleUpdateStatus(report.id, "revisado")}
                >
                  <Eye className="mr-2 h-4 w-4" />
                  Marcar como revisado
                </Button>
              )}
              <Button
                variant="outline"
                className="border-red-400/30 text-red-400 hover:bg-red-500/10 hover:text-red-300"
                onClick={() =>
                  handleUpdateStatus(report.id, "resuelto", "delete_content")
                }
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar contenido
              </Button>
              {reported_user && !reported_user.suspended_at && (
                <Button
                  variant="outline"
                  className="border-orange-400/30 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300"
                  onClick={() =>
                    handleUpdateStatus(report.id, "resuelto", "suspend_user")
                  }
                >
                  <Ban className="mr-2 h-4 w-4" />
                  Suspender usuario
                </Button>
              )}
              <Button
                variant="outline"
                className="border-green-400/30 text-green-400 hover:bg-green-500/10 hover:text-green-300"
                onClick={() => handleUpdateStatus(report.id, "resuelto")}
              >
                <CheckCircle className="mr-2 h-4 w-4" />
                Resolver sin acción
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </AdminLayout>
  )
}
